/**
 * Hilfsfunktionen für die hybride Projektsuche.
 *
 * Die Projektnummern-Erkennung spiegelt die Backend-Logik in
 * `api/projekt_suche.py` (normalize_query): Nummern-Anfragen lösen die Suche
 * sofort aus (ohne Debounce) und sind von der Mindestlänge ausgenommen.
 */

/** Volle Projektnummer: "{ordner}#{nummer}" oder Anzeigeform mit Jahr. */
const PROJEKT_IDENT_RE = /^[^\s#]+#\d+(?:-\d{4})?$/;

/** Unvollständige Projektnummer / Ordner-Pfad: "1-", "1-1", "1-18", "2-10-3". */
const PROJEKT_NUMMER_PREFIX_RE = /^(?:\d+(?:-\d*)*-|\d+(?:-\d+)+)$/;

/**
 * Angebots-/Belegnummern (App-Kreis und Hero), Spiegel von
 * `api/projekt_suche.py` (GD_NUMMER_RE): AN26-0042, RE-681, AN-2026-0012.
 */
const GD_NUMMER_RE =
  /^(?:(?:an|re|ls|ab|sl|ma|nw)\d{2}(?:-\d*)?(?:\.\d+)?|(?:an|re|ls|ab|sl|ma|nw|a|r)-\d[\d-]*(?:\.\d+)?)$/i;

/** Mindestlänge für freie Textsuche (Nummern-Anfragen sind ausgenommen). */
export const SEARCH_MIN_QUERY_LENGTH = 2;

/** True, wenn die Eingabe wie eine Projekt-/Ordnernummer aussieht. */
export function isProjectNumberQuery(raw: string): boolean {
  const q = raw.trim().toLowerCase().replace(/\s*#\s*/g, "#");
  if (!q || q.includes(" ")) return false;
  return q.includes("#") || PROJEKT_IDENT_RE.test(q) || PROJEKT_NUMMER_PREFIX_RE.test(q);
}

/** True, wenn die Eingabe wie eine Angebots- oder Belegnummer aussieht. */
export function isDocumentNumberQuery(raw: string): boolean {
  const q = raw.trim().toLowerCase();
  if (!q || q.includes(" ")) return false;
  return GD_NUMMER_RE.test(q);
}

/** Projekt-, Ordner- oder Belegnummer – Sofortsuche ohne Debounce. */
export function isIdentNumberQuery(raw: string): boolean {
  return isProjectNumberQuery(raw) || isDocumentNumberQuery(raw);
}

/**
 * True, wenn die Eingabe eine Suche auslösen darf: leer (= Filter löschen),
 * Projekt-/Belegnummer (sofort) oder mindestens SEARCH_MIN_QUERY_LENGTH Zeichen.
 */
export function isSearchQueryReady(raw: string): boolean {
  const q = raw.trim();
  if (!q) return true;
  if (isIdentNumberQuery(q)) return true;
  return q.length >= SEARCH_MIN_QUERY_LENGTH;
}

/** Strukturierte Artikelnummer: Segmente aus [a-z0-9] mit Trennzeichen. */
const ARTIKEL_NUMMER_RE = /^[a-z0-9]+(?:[-._/][a-z0-9]+)+$/;

/**
 * True, wenn die Eingabe wie eine Artikel-Identnummer aussieht: EAN/GTIN
 * (8/12–14 Ziffern), reine Nummern ab 5 Stellen oder strukturierte
 * Artikelnummern ("ART-2026-00125"). Spiegelt die Backend-Erkennung in
 * `api/artikel_suche.py` (normalize_query): solche Anfragen (v. a. Barcode-
 * Scanner) suchen sofort ohne Debounce.
 */
export function isArticleCodeQuery(raw: string): boolean {
  const q = raw.trim().toLowerCase();
  if (!q || q.includes(" ")) return false;
  const compact = q.replace(/[^a-z0-9]/g, "");
  if (/^\d{8}$|^\d{12,14}$/.test(compact)) return true;
  if (/^\d{5,}$/.test(compact)) return true;
  return ARTIKEL_NUMMER_RE.test(q) && /\d/.test(q) && compact.length >= 5;
}

export interface HighlightPart {
  text: string;
  hit: boolean;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Zerlegt einen Text in hervorzuhebende und normale Abschnitte, basierend auf
 * den Wörtern der Suchanfrage (case-insensitiv, rein textbasiert – kein HTML).
 */
export function splitForHighlight(text: string, query: string): HighlightPart[] {
  if (!text) return [];
  const tokens = query
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter((t) => t.length >= 2)
    .map(escapeRegExp);
  if (tokens.length === 0) return [{ text, hit: false }];

  let re: RegExp;
  try {
    re = new RegExp(tokens.join("|"), "gi");
  } catch {
    return [{ text, hit: false }];
  }

  const parts: HighlightPart[] = [];
  let lastIndex = 0;
  for (const match of text.matchAll(re)) {
    const index = match.index ?? 0;
    if (!match[0]) continue;
    if (index > lastIndex) {
      parts.push({ text: text.slice(lastIndex, index), hit: false });
    }
    parts.push({ text: match[0], hit: true });
    lastIndex = index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push({ text: text.slice(lastIndex), hit: false });
  }
  return parts.length > 0 ? parts : [{ text, hit: false }];
}
